import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  LinearProgress,
} from "@mui/material";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import { axiosConfig } from "../../axios/axiosConfig";

const SectionHeader = ({ icon, title, subtitle }) => (
  <Box
    sx={{
      background: "#04343a",
      borderRadius: "10px 10px 0 0",
      px: 2.5,
      py: 1.5,
      display: "flex",
      alignItems: "center",
      gap: 1.5,
    }}
  >
    {icon}
    <Box>
      <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: 15 }}>
        {title}
      </Typography>
      {subtitle && (
        <Typography sx={{ color: "rgba(255,255,255,0.65)", fontSize: 12 }}>
          {subtitle}
        </Typography>
      )}
    </Box>
  </Box>
);

const PlanChangeList = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await axiosConfig({
          method: "get",
          url: "/subscription/plan-change-list",
        });
        setData(response?.data?.response || []);
      } catch (error) {
        console.error("plan-change-list error:", error);
        setData([]);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const formatDate = (val) =>
    val
      ? new Date(val).toLocaleDateString("en-IN", {
          day: "numeric",
          month: "short",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        })
      : "-";

  const COLUMNS = [
    "ID",
    "Plan Change",
    "Days Extended",
    "Timedelta (days)",
    "Renewal Count",
    "Tokens",
    "Total Users",
    "Success",
    "Failed",
    "Remark",
    "Created At",
  ];

  return (
    <Box sx={{ p: 3, background: "#eef2f7", minHeight: "100vh" }}>
      {/* Page Header */}
      <Box
        sx={{
          background: "#04343a",
          borderRadius: "12px",
          px: 2,
          py: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 2,
          boxShadow: "0 4px 20px rgba(4,52,58,0.3)",
          mb: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <SwapHorizIcon sx={{ color: "#fff", fontSize: 34 }} />
          <Box>
            <Typography
              sx={{ color: "#fff", fontWeight: 800, fontSize: 22, lineHeight: 1.2 }}
            >
              Plan Change List
            </Typography>
            <Typography sx={{ color: "rgba(255,255,255,0.65)", fontSize: 13 }}>
              All bulk plan upgrades and downgrades
            </Typography>
          </Box>
        </Box>
        <Chip
          label={`Total: ${data.length}`}
          sx={{
            background: "rgba(255,255,255,0.15)",
            color: "#fff",
            fontWeight: 600,
            fontSize: 12,
          }}
        />
      </Box>

      {/* Table Card */}
      <Box
        sx={{
          background: "#fff",
          borderRadius: "12px",
          border: "1px solid #e2e8f0",
          boxShadow: "0 1px 6px rgba(0,0,0,0.07)",
          overflow: "hidden",
        }}
      >
        <SectionHeader
          icon={<SwapHorizIcon sx={{ color: "#fff", fontSize: 20 }} />}
          title="Plan Change Records"
          subtitle={`${data.length} record${data.length !== 1 ? "s" : ""}`}
        />

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ background: "#f8fafc" }}>
                {COLUMNS.map((h) => (
                  <TableCell
                    key={h}
                    sx={{
                      fontSize: 12,
                      fontWeight: 700,
                      color: "#475569",
                      whiteSpace: "nowrap",
                      borderBottom: "2px solid #e2e8f0",
                      py: 1.2,
                    }}
                  >
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={COLUMNS.length} align="center" sx={{ py: 4 }}>
                    <LinearProgress
                      sx={{
                        maxWidth: 300,
                        mx: "auto",
                        borderRadius: 2,
                        "& .MuiLinearProgress-bar": { bgcolor: "#016551" },
                      }}
                    />
                    <Typography sx={{ fontSize: 13, color: "#94a3b8", mt: 1 }}>
                      Loading...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : data.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={COLUMNS.length}
                    align="center"
                    sx={{ py: 4, color: "#94a3b8", fontSize: 14 }}
                  >
                    No plan change records found.
                  </TableCell>
                </TableRow>
              ) : (
                data.map((row, idx) => (
                  <TableRow
                    key={row.id}
                    sx={{
                      "&:hover": { background: "#f8fafc" },
                      background: idx % 2 === 0 ? "#fff" : "#fafcff",
                    }}
                  >
                    {/* ID */}
                    <TableCell sx={{ fontSize: 12, color: "#64748b", fontWeight: 600 }}>
                      {row.id}
                    </TableCell>

                    {/* Plan Change: Old → New */}
                    <TableCell sx={{ py: 1.5 }}>
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.4 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#DC2626", minWidth: 32 }}>old:</Typography>
                          <Typography sx={{ fontSize: 12, color: "#1e293b" }}>{row.old_plan_name || "-"}</Typography>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#016551", minWidth: 32 }}>new:</Typography>
                          <Typography sx={{ fontSize: 12, color: "#1e293b" }}>{row.new_plan_name || "-"}</Typography>
                        </Box>
                      </Box>
                    </TableCell>

                    {/* Days Extended */}
                    <TableCell>
                      <Chip
                        label={row.days_extend != null ? `${row.days_extend} days` : "-"}
                        size="small"
                        sx={{
                          fontSize: 11,
                          fontWeight: 700,
                          background: row.days_extend != null ? "#edfaf3" : "#f1f5f9",
                          color: row.days_extend != null ? "#016551" : "#94a3b8",
                          height: 22,
                        }}
                      />
                    </TableCell>

                    {/* Timedelta */}
                    <TableCell sx={{ fontSize: 12, color: "#475569" }}>
                      {row.timedelta_value != null ? `${row.timedelta_value} days` : "-"}
                    </TableCell>

                    {/* Renewal Count */}
                    <TableCell>
                      <Chip
                        label={row.renewal_count_after_extend ?? "-"}
                        size="small"
                        sx={{
                          fontSize: 11,
                          fontWeight: 700,
                          background: "#f0f9ff",
                          color: "#0369a1",
                          height: 22,
                        }}
                      />
                    </TableCell>

                    {/* Tokens */}
                    <TableCell sx={{ fontSize: 12, color: "#475569" }}>
                      {row.tokens ?? "-"}
                    </TableCell>

                    {/* Total Users */}
                    <TableCell>
                      <Chip
                        label={row.total_users ?? "-"}
                        size="small"
                        sx={{
                          fontSize: 11,
                          fontWeight: 700,
                          background: "#f0f9ff",
                          color: "#0369a1",
                          height: 22,
                        }}
                      />
                    </TableCell>

                    {/* Success */}
                    <TableCell>
                      <Chip
                        label={row.success_count ?? "-"}
                        size="small"
                        sx={{
                          fontSize: 11,
                          fontWeight: 700,
                          background: "#edfaf3",
                          color: "#016551",
                          height: 22,
                        }}
                      />
                    </TableCell>

                    {/* Failed */}
                    <TableCell>
                      <Chip
                        label={row.failed_count ?? "-"}
                        size="small"
                        sx={{
                          fontSize: 11,
                          fontWeight: 700,
                          background: row.failed_count > 0 ? "#fef2f2" : "#f1f5f9",
                          color: row.failed_count > 0 ? "#DC2626" : "#94a3b8",
                          height: 22,
                        }}
                      />
                    </TableCell>

                    {/* Remark */}
                    <TableCell
                      sx={{
                        fontSize: 12,
                        color: "#64748b",
                        maxWidth: 180,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {row.remark || "-"}
                    </TableCell>

                    {/* Created At */}
                    <TableCell sx={{ fontSize: 12, color: "#475569", whiteSpace: "nowrap" }}>
                      {formatDate(row.created_at)}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Box sx={{ px: 2.5, py: 1.5, borderTop: "1px solid #f1f5f9" }}>
          <Typography sx={{ fontSize: 12, color: "#94a3b8" }}>
            {data.length} total record{data.length !== 1 ? "s" : ""}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default PlanChangeList;